import React, {
 createContext,
 useContext,
 useState,
 useEffect,
 useCallback,
 useRef,
} from "react";
import { io, Socket } from "socket.io-client";
import { useApi } from "./ApiContext";
import { useAuth } from "./AuthContext";
import { playNotificationSound } from "../utils/notificationSound";

interface ChatMessage {
 _id: string;
 conversation: string;
 sender: {
  _id: string;
  name: string;
  email: string;
 };
 senderType: "customer" | "agent";
 message: string;
 attachments?: Array<{
  url: string;
  filename: string;
  mimeType: string;
 }>;
 isRead: boolean;
 createdAt: string;
}

interface ChatConversation {
 _id: string;
 user?: string;
 email?: string;
 status: "open" | "in-progress" | "resolved" | "closed";
 assignedTo?: {
  _id: string;
  name: string;
  email: string;
 };
 subject?: string;
 lastMessageAt: string;
 unreadCount: number;
}

interface ChatContextType {
 socket: Socket | null;
 currentConversation: ChatConversation | null;
 conversations: ChatConversation[];
 messages: ChatMessage[];
 loading: boolean;
 isOpen: boolean;
 unreadCount: number;
 openChat: () => void;
 closeChat: () => void;
 createOrGetConversation: (subject?: string) => Promise<void>;
 sendMessage: (message: string, attachments?: any[]) => Promise<void>;
 loadMessages: (conversationId: string) => Promise<void>;
 loadConversations: () => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({
 children,
}) => {
 const { api } = useApi();
 const { user } = useAuth();
 const [socket, setSocket] = useState<Socket | null>(null);
 const [currentConversation, setCurrentConversation] =
  useState<ChatConversation | null>(null);
 const [conversations, setConversations] = useState<ChatConversation[]>([]);
 const [messages, setMessages] = useState<ChatMessage[]>([]);
 const [loading, setLoading] = useState(false);
 const [isOpen, setIsOpen] = useState(false);
 const [unreadCount, setUnreadCount] = useState(0);
 
 // Use refs to track latest state in socket handlers
 const isOpenRef = useRef(isOpen);
 const currentConversationRef = useRef(currentConversation);
 const conversationsRef = useRef(conversations);
 const socketRef = useRef<Socket | null>(null);
 
 // Keep refs in sync with state
 useEffect(() => {
  isOpenRef.current = isOpen;
 }, [isOpen]);
 
 useEffect(() => {
  currentConversationRef.current = currentConversation;
 }, [currentConversation]);
 
 useEffect(() => {
  conversationsRef.current = conversations;
 }, [conversations]);
 
 useEffect(() => {
  socketRef.current = socket;
 }, [socket]);

 // Initialize socket connection
 useEffect(() => {
  const token = localStorage.getItem("token");
  if (token && user) {
   // Get backend URL based on current hostname (for localhost subdomain testing)
   let backendUrl = process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:5100";
   
   // For localhost subdomain testing, use current hostname with backend port
   if (typeof window !== "undefined" && !process.env.REACT_APP_BACKEND_BASE_URL) {
    const hostname = window.location.hostname;
    if (hostname.includes("localhost") || hostname.includes("local")) {
     backendUrl = `http://${hostname}:5100`;
    } else {
     backendUrl = `${window.location.protocol}//${hostname}`;
    }
   }
   
   // Remove trailing slash if present
   const normalizedUrl = backendUrl.replace(/\/$/, "");
   
   const newSocket = io(normalizedUrl, {
    path: "/socket.io/chat",
    auth: { token },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
   });

   newSocket.on("connect", () => {
    // Socket connected - join all conversation rooms if they're already loaded
    // This ensures we receive messages even when chat is closed
    // Use ref to get latest conversations
    const currentConversations = conversationsRef.current;
    if (currentConversations.length > 0) {
     currentConversations.forEach((conv) => {
      const convId = typeof conv._id === "string" ? conv._id : String(conv._id);
      newSocket.emit("join-conversation", convId);
     });
    } else {
      if (process.env.NODE_ENV === "development") {
        console.log("No conversations to join on connect");
      }
    }
   });

   newSocket.on("connect_error", (error) => {
    console.error("Chat connection error:", error);
   });

   newSocket.on("error", (error: any) => {
    console.error("Chat socket error:", error);
    // Handle error - could show a toast notification here
   });

   newSocket.on("new-message", (message: ChatMessage) => {
    // Only update messages if chat is open and it's the current conversation
    // Otherwise, we just update conversations list and play sound
    if (isOpenRef.current && currentConversationRef.current) {
     const currentConvId = typeof currentConversationRef.current._id === "string" 
      ? currentConversationRef.current._id 
      : String(currentConversationRef.current._id);
     const messageConvId = typeof message.conversation === "string" 
      ? message.conversation 
      : String(message.conversation);
     
     if (currentConvId === messageConvId) {
      setMessages((prev) => {
       // Check if there's a temporary message with the same content and replace it
       const tempIndex = prev.findIndex(
        (msg) => msg._id.startsWith("temp-") && msg.message === message.message
       );
       if (tempIndex !== -1) {
        // Replace temporary message with real one
        const newMessages = [...prev];
        newMessages[tempIndex] = message;
        return newMessages;
       }
       // Check if message already exists (avoid duplicates)
       const exists = prev.some((msg) => msg._id === message._id);
       if (exists) {
        return prev;
       }
       return [...prev, message];
      });
     }
    }
    
    // Update conversation unread count if message is from agent
    if (message.senderType === "agent") {
     const conversationId = typeof message.conversation === "string" 
      ? message.conversation 
      : (message.conversation as any)?._id || String(message.conversation);
     
     // Check if chat is open for this conversation
     const isCurrentConversation = isOpenRef.current && currentConversationRef.current && (() => {
      const currentConvId = typeof currentConversationRef.current._id === "string" 
       ? currentConversationRef.current._id 
       : String(currentConversationRef.current._id);
      return currentConvId === conversationId;
     })();
     
     // Play notification sound if chat is closed OR if tab is not in focus
     // This helps keep conversations flowing even when user is on other tabs
     if (!isOpenRef.current || document.hidden) {
      playNotificationSound();
     }
     
     // If chat is open for this conversation, mark as read immediately
     if (isCurrentConversation && newSocket) {
      newSocket.emit("mark-read", conversationId);
     }
     
     setConversations((prev) => {
      const conversationExists = prev.some((conv) => {
       const convId = typeof conv._id === "string" ? conv._id : String(conv._id);
       return convId === conversationId;
      });
      
      if (conversationExists) {
       return prev.map((conv) => {
        const convId = typeof conv._id === "string" ? conv._id : String(conv._id);
        if (convId === conversationId) {
         // Only increment unread count if chat is closed or it's a different conversation
         const shouldIncrement = !isCurrentConversation;
         return {
          ...conv,
          unreadCount: shouldIncrement ? (conv.unreadCount || 0) + 1 : (conv.unreadCount || 0),
          lastMessageAt: message.createdAt,
         };
        }
        return conv;
       });
      }
      
      // Conversation not in list, reload to get it
      setTimeout(() => {
       loadConversations();
      }, 100);
      return prev;
     });
    }
   });

   newSocket.on("disconnect", () => {
    // Socket disconnected
   });

   setSocket(newSocket);
   socketRef.current = newSocket; // Update ref immediately

   return () => {
    newSocket.close();
    socketRef.current = null;
   };
  }
  return () => {};
 }, [user]);

 const loadConversations = useCallback(async () => {
  try {
   const response = await api.get("/chat/conversations");
   const loadedConversations = response.data.conversations || [];
   setConversations(loadedConversations);
   conversationsRef.current = loadedConversations; // Update ref immediately
   
   // Join all conversation rooms to receive messages even when chat is closed
   // Use ref to get latest socket
   const currentSocket = socketRef.current;
   if (currentSocket && currentSocket.connected) {
    loadedConversations.forEach((conv: ChatConversation) => {
     const convId = typeof conv._id === "string" ? conv._id : String(conv._id);
     currentSocket.emit("join-conversation", convId);
    });
   }
  } catch (error) {
   console.error("Error loading conversations:", error);
  }
 }, [api]);

 // Load conversations on mount
 useEffect(() => {
  if (user) {
   loadConversations();
  }
 }, [user, loadConversations]);

 // Join all conversation rooms when conversations are loaded or socket connects
 // This ensures we receive messages even when chat is closed
 useEffect(() => {
  const currentSocket = socketRef.current;
  if (currentSocket && currentSocket.connected && conversations.length > 0) {
   conversations.forEach((conv) => {
    const convId = typeof conv._id === "string" ? conv._id : String(conv._id);
    currentSocket.emit("join-conversation", convId);
   });
  }
 }, [socket, conversations]);

 // Calculate total unread count from conversations
 useEffect(() => {
  const total = conversations.reduce(
   (sum, conv) => sum + (conv.unreadCount || 0),
   0
  );
  setUnreadCount(total);
 }, [conversations]);


 const loadMessages = useCallback(
  async (conversationId: string) => {
   try {
    setLoading(true);
    const response = await api.get(
     `/chat/conversations/${conversationId}/messages`
    );
    setMessages(response.data.messages || []);

    // Mark as read
    if (socket) {
     socket.emit("mark-read", conversationId);
    }
   } catch (error) {
    console.error("Error loading messages:", error);
   } finally {
    setLoading(false);
   }
  },
  [api, socket]
 );

 const createOrGetConversation = useCallback(
  async (subject?: string) => {
   try {
    setLoading(true);
    const response = await api.post("/chat/conversations", { subject });
    const conversation = response.data;
    setCurrentConversation(conversation);

    // Join socket room
    const currentSocket = socketRef.current;
    if (currentSocket && currentSocket.connected) {
     const convId = typeof conversation._id === "string" ? conversation._id : String(conversation._id);
     currentSocket.emit("join-conversation", convId);
    }

    // Add conversation to list if not already there
    setConversations((prev) => {
     const exists = prev.some((conv) => {
      const convId = typeof conv._id === "string" ? conv._id : String(conv._id);
      const newConvId = typeof conversation._id === "string" ? conversation._id : String(conversation._id);
      return convId === newConvId;
     });
     if (!exists) {
      return [...prev, conversation];
     }
     return prev;
    });

    // Load messages
    await loadMessages(conversation._id);
   } catch (error) {
    console.error("Error creating conversation:", error);
   } finally {
    setLoading(false);
   }
  },
  [api, loadMessages]
 );

 const sendMessage = useCallback(
  async (message: string, attachments?: any[]) => {
   // Ensure we have a conversation - create one if needed
   let conversation = currentConversation;
   if (!conversation && user) {
    try {
     setLoading(true);
     const response = await api.post("/chat/conversations", {});
     conversation = response.data;
     if (!conversation) {
      console.error("Failed to create conversation");
      setLoading(false);
      return;
     }
     setCurrentConversation(conversation);
     
     // Join socket room
     if (socket) {
      socket.emit("join-conversation", conversation._id);
     }
     
     // Load existing messages
     await loadMessages(conversation._id);
    } catch (error) {
     console.error("Error creating conversation:", error);
     setLoading(false);
     return;
    } finally {
     setLoading(false);
    }
   }

   if (!conversation || !socket) {
    console.error("Cannot send message: missing conversation or socket");
    return;
   }

   // Ensure socket is connected
   if (!socket.connected) {
    console.error("Socket is not connected");
    return;
   }

   // Ensure we're joined to the conversation room
   socket.emit("join-conversation", conversation._id);

   const tempMessageId = `temp-${Date.now()}`;
   
   try {
    // Optimistically add message
    const tempMessage: ChatMessage = {
     _id: tempMessageId,
     conversation: conversation._id,
     sender: {
      _id: user?._id || "",
      name: user?.name || "",
      email: user?.email || "",
     },
     senderType: "customer",
     message,
     attachments,
     isRead: false,
     createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMessage]);

    // Send message via socket
    socket.emit("send-message", {
     conversationId: conversation._id,
     message,
     attachments,
    });
   } catch (error) {
    console.error("Error sending message:", error);
    // Remove temporary message on error
    setMessages((prev) => prev.filter((msg) => msg._id !== tempMessageId));
   }
  },
  [currentConversation, socket, user, api, loadMessages]
 );

 const openChat = useCallback(async () => {
  setIsOpen(true);
  if (!currentConversation && user) {
   await createOrGetConversation();
  } else if (currentConversation && socket) {
   // Ensure we're joined to the conversation when opening
   socket.emit("join-conversation", currentConversation._id);
   // Reload messages to get latest
   await loadMessages(currentConversation._id);
   // Mark messages as read when opening chat
   socket.emit("mark-read", currentConversation._id);
   // Reload conversations to get updated unread counts
   await loadConversations();
  }
 }, [currentConversation, user, createOrGetConversation, socket, loadMessages, loadConversations]);

 const closeChat = useCallback(() => {
  setIsOpen(false);
  // Don't leave the conversation room - we want to keep receiving messages when chat is closed
  // The room will be cleaned up when the socket disconnects
 }, []);

 return (
  <ChatContext.Provider
   value={{
    socket,
    currentConversation,
    conversations,
    messages,
    loading,
    isOpen,
    unreadCount,
    openChat,
    closeChat,
    createOrGetConversation,
    sendMessage,
    loadMessages,
    loadConversations,
   }}
  >
   {children}
  </ChatContext.Provider>
 );
};

export const useChat = () => {
 const context = useContext(ChatContext);
 if (context === undefined) {
  throw new Error("useChat must be used within a ChatProvider");
 }
 return context;
};
